import React, { useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import { Button, Col, Empty, message, Row, Table } from "antd";
import { EditOutlined } from "@ant-design/icons";

import { patchAdviseeInvestmentPortfolio } from "apis/advisee";
import {
  constructDisplayPrice,
  constructDisplayPercentage,
  constructShortenedFinancialNumber,
} from "utils/general";
import { buildPIPSummaryUri } from "utils/routes";

import InfoCard from "components/InfoCard/InfoCard";
import Title from "components/Title/Title";
import SectionContainer from "components/SectionContainer/SectionContainer";

import AddResourceModal from "./components/AddResourceModal/AddResourceModal";

import {
  FinancialValueText,
  Label,
  SummaryValueText,
  ResourceTypeLabel,
} from "./Resources.styles";
import Locale from "react-intl-universal";

const constructSummaryDataSource = (summaryInfo) => [
  {
    label: Locale.get("investmentPortfolio.tProjectedNetValue"),
    value: constructDisplayPrice(summaryInfo.projectedNet),
  },
  {
    label: Locale.get("investmentPortfolio.shortage"),
    value: constructDisplayPrice(summaryInfo.shortage),
  },
  {
    label: Locale.get("investmentPortfolio.availableFunds"),
    value: summaryInfo.monthlyAvailableFunds,
    // value: constructDisplayPrice(summaryInfo.monthlyAvailableFunds)
  },
];

const getInsuranceTableColumns = [
  {
    dataIndex: "label",
    key: "label",
    render: (value) => <Label>{value}</Label>,
  },
  {
    dataIndex: "value",
    key: "value",
    render: (value) => <FinancialValueText>{value}</FinancialValueText>,
  },
];

const getNumberRender = (value, record, isPrice) => ({
  children: value
    ? isPrice
      ? constructDisplayPrice(value)
      : constructDisplayPercentage(value)
    : "-",
  ...(!record.type && { props: { colSpan: 0 } }),
});

const SummaryCard = ({ title, value, isText }) => {
  return (
    <Col span={12} xl={8}>
      <InfoCard title={title}>
        <SummaryValueText isText={isText}>{value}</SummaryValueText>
      </InfoCard>
    </Col>
  );
};

const Resources = ({
  goalSetting = {},
  summaryInfo = {},
  resources = [],
  setShowManageGoalModal,
}) => {
  const history = useHistory();
  const { id: adviseeId } = useParams();
  const [showAddResourceModal, setShowAddResourceModal] = useState(false);
  const [selectedRowIndex, setSelectedRowIndex] = useState(-1);

  const isGoalSet = !!Object.values(goalSetting).filter((val) => val).length;

  const updateInvestmentPortfolio = (payload, successMessage) => {
    patchAdviseeInvestmentPortfolio(adviseeId, "resources", payload)
      .then(() => {
        message.success(successMessage);
        handleOnModalCancelButtonClick();
      })
      .catch(() => {
        message.error(
          "Error in updating investment portfolio details. Please check your connection and try again."
        );
      });
  };

  const handleOnRowSelect = (index) => {
    setSelectedRowIndex(index);
    setShowAddResourceModal(true);
  };

  const handleOnResourceModalOk = (resource) => {
    if (selectedRowIndex > -1) {
      const editedResource = [...resources];
      editedResource[selectedRowIndex] = resource;
      updateInvestmentPortfolio(
        editedResource,
        Locale.get("investmentPortfolio.updateSuccess")
      );
    } else {
      const newResources = [...resources, resource];
      updateInvestmentPortfolio(
        newResources,
        Locale.get("investmentPortfolio.newResourceAdded")
      );
    }
  };

  const handleOnModalRemoveButtonClick = () => {
    const newResources = [...resources];
    newResources.splice(selectedRowIndex, 1);
    updateInvestmentPortfolio(
      newResources,
      Locale.get("investmentPortfolio.removeSuccess")
    );
  };

  const handleOnModalCancelButtonClick = () => {
    setShowAddResourceModal(false);
    setSelectedRowIndex(-1);
  };

  const handleOnClickSetGoalButton = () => {
    history.push(buildPIPSummaryUri(adviseeId));
    setShowManageGoalModal(true);
  };

  const getResourcesTableColumns = [
    {
      title: Locale.get("investmentPortfolio.resources"),
      dataIndex: "label",
      key: "label",
      render: (value, record) => {
        if (!record.type) {
          return {
            children: <ResourceTypeLabel>{value}</ResourceTypeLabel>,
            props: {
              colSpan: 7,
              style: {
                background: "#F5F8FF",
              },
            },
          };
        }
        return value;
      },
    },
    {
      title: Locale.get("investmentPortfolio.currentValue"),
      dataIndex: "currentValue",
      key: "currentValue",
      render: getNumberRender,
    },
    {
      title: Locale.get("investmentPortfolio.ror"),
      dataIndex: "returnRate",
      key: "returnRate",
      render: (value, record) => getNumberRender(value, record, false),
    },
    {
      title: Locale.get("investmentPortfolio.futureValueRetirement"),
      dataIndex: "futureValue",
      key: "futureValue",
      render: getNumberRender,
    },
    {
      title: Locale.get("investmentPortfolio.outstandingDebt"),
      dataIndex: "outstandingDebt",
      key: "outstandingDebt",
      render: getNumberRender,
    },
    {
      title: Locale.get("investmentPortfolio.projectedNetValue"),
      dataIndex: "netValue",
      key: "netValue",
      render: getNumberRender,
    },
  ];

  return (
    <>
      {isGoalSet ? (
        <>
          <SectionContainer>
            <Row
              justify="space-between"
              align="middle"
              style={{ marginBottom: 24 }}
            >
              <Col>
                <Title>Resource</Title>
              </Col>
              <Col>
                <Button
                  type="primary"
                  icon={<EditOutlined />}
                  style={{ float: "right" }}
                  onClick={() => setShowAddResourceModal(true)}
                >
                  {Locale.get("investmentPortfolio.addNewResource")}
                </Button>
              </Col>
            </Row>
            <Row gutter={[16, 16]}>
              <SummaryCard
                title={Locale.get("investmentPortfolio.goalDesc")}
                value={goalSetting.description || "-"}
                isText
              ></SummaryCard>
              <SummaryCard
                title={Locale.get("investmentPortfolio.goalAmount")}
                value={constructShortenedFinancialNumber(
                  goalSetting.goalAmt,
                  false
                )}
              ></SummaryCard>
              <SummaryCard
                title={Locale.get("investmentPortfolio.yearToAchieve")}
                value={goalSetting.yearsToAchieve || 0}
              ></SummaryCard>
            </Row>
          </SectionContainer>
          <SectionContainer>
            <Table
              dataSource={constructSummaryDataSource(summaryInfo)}
              columns={getInsuranceTableColumns}
              pagination={false}
              showHeader={false}
              rowKey={(record) => record.label}
              style={{ width: "100%" }}
            />
          </SectionContainer>
          <SectionContainer>
            <Table
              dataSource={resources}
              columns={getResourcesTableColumns}
              pagination={false}
              // rowKey={(record) => record.label}
              style={{ width: "100%" }}
              scroll={{ x: true }}
              onRow={(record, index) => ({
                onClick: () => record.type && handleOnRowSelect(index),
              })}
            />
          </SectionContainer>
          {showAddResourceModal && (
            <AddResourceModal
              defaultValues={resources[selectedRowIndex]}
              visible={showAddResourceModal}
              onCancel={handleOnModalCancelButtonClick}
              onOk={handleOnResourceModalOk}
              onRemove={handleOnModalRemoveButtonClick}
            />
          )}
        </>
      ) : (
        <Empty
          style={{ paddingTop: 150 }}
          description="Oops! Seems like you have not set your goal yet!"
        >
          <Button type="primary" onClick={handleOnClickSetGoalButton}>
            Set my goal now
          </Button>
        </Empty>
      )}
    </>
  );
};

export default Resources;
